import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import AnimatedHeading from "../../animations/AnimatedHeading";
import { ArrowUpRight, CheckCircle2, X } from "lucide-react";

/* ============================================================
   ANIMATION
============================================================ */

const fadeUp = (delay = 0) => ({
  hidden: {
    opacity: 0,
    y: 24,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  },
});

/* ============================================================
   INPUT STYLE
============================================================ */

const inputStyle = {
  background: "var(--card-bg)",
  border: "1px solid var(--border-primary)",
  borderRadius: 10,
  color: "var(--text-primary)",
  padding: "14px 18px",
  fontSize: "0.9rem",
  width: "100%",
  outline: "none",
  fontFamily: "'DM Sans', sans-serif",
  transition:
    "border-color .2s, box-shadow .2s, background-color .3s ease",
};

/* ============================================================
   SERVICE DATA
============================================================ */

const serviceData = {
  "Software Development": [
    "Custom Software Development",
    "Mobile App Development",
    "Web Development",
    "UI/UX Design",
    "CRM Solutions",
    "MLM Software",
  ],

  "API Services": [
    "WhatsApp API Services",
    "SMS OTP Services",
  ],

  "Legal Services": [
    "Business Registration",
    "Company Registration",
    "Taxation Services",
    "ISO Certification",
    "IPR Services",
  ],
};

/* ============================================================
   FIELD
============================================================ */

function Field({
  as: Tag = "input",
  placeholder,
  type = "text",
  value,
  onChange,
  name,
  required = false,
  ...rest
}) {
  const [focused, setFocused] = useState(false);

  return (
    <Tag
      type={Tag === "input" ? type : undefined}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...inputStyle,

        borderColor: focused
          ? "var(--brand-primary)"
          : "var(--border-primary)",

        boxShadow: focused
          ? "0 0 0 3px color-mix(in srgb, var(--brand-primary) 10%, transparent)"
          : "none",

        resize: Tag === "textarea" ? "none" : undefined,
      }}
      className="
        w-full
        min-w-0
        text-sm
        transition-all
        duration-200
        min-[375px]:text-[14px]
        sm:text-sm
        md:text-[15px]
        lg:text-[0.9rem]
        placeholder:text-[var(--text-muted)]
      "
      {...rest}
    />
  );
}

/* ============================================================
   SELECT FIELD
============================================================ */

function SelectField({
  placeholder,
  options,
  value,
  onChange,
  name,
  onOpen,
  onClose,
}) {
  const [focused, setFocused] = useState(false);

  const handleOpen = () => {
    onOpen?.();
  };

  const handleFocus = () => {
    setFocused(true);
    onOpen?.();
  };

  const handleBlur = () => {
    setFocused(false);

    setTimeout(() => {
      onClose?.();
    }, 100);
  };

  const handleChange = (e) => {
    onChange(e);
    onClose?.();
  };

  return (
    <select
      name={name}
      value={value}
      onChange={handleChange}
      onMouseDown={handleOpen}
      onTouchStart={handleOpen}
      onPointerDown={handleOpen}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{
        ...inputStyle,

        borderColor: focused
          ? "var(--brand-primary)"
          : "var(--border-primary)",

        boxShadow: focused
          ? "0 0 0 3px color-mix(in srgb, var(--brand-primary) 10%, transparent)"
          : "none",

        appearance: "none",
        cursor: "pointer",
      }}
      className="
        w-full
        min-w-0
        text-sm
        transition-all
        duration-200
        min-[375px]:text-[14px]
        sm:text-sm
        md:text-[15px]
        lg:text-[0.9rem]
      "
    >
      <option
        value=""
        disabled
        style={{
          background: "var(--card-bg)",
          color: "var(--text-muted)",
        }}
      >
        {placeholder}
      </option>

      {options.map((option, index) => (
        <option
          key={index}
          value={option}
          style={{
            background: "var(--card-bg)",
            color: "var(--text-primary)",
          }}
        >
          {option}
        </option>
      ))}
    </select>
  );
}

/* ============================================================
   SERVICE SELECT
============================================================ */

function ServiceSelect({
  placeholder,
  value,
  onChange,
  options,
  name,
  onOpen,
  onClose,
}) {
  return (
    <div className="relative w-full">
      <SelectField
        name={name}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        onOpen={onOpen}
        onClose={onClose}
      />

      {/* CUSTOM CHEVRON */}

      <span
        className="
          pointer-events-none
          absolute
          right-3
          top-1/2
          flex
          -translate-y-1/2
          items-center
          justify-center
          min-[375px]:right-4
        "
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-[var(--text-muted)]"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </span>
    </div>
  );
}

/* ============================================================
   CONTACT FORM
============================================================ */

export default function Contactform() {
  /* ==========================================================
     FORM STATE
  ========================================================== */

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    subService: "",
    message: "",
  });

  /* ==========================================================
     LOADING
  ========================================================== */

  const [loading, setLoading] = useState(false);

  /* ==========================================================
     SUCCESS CARD
  ========================================================== */

  const [submitted, setSubmitted] = useState(false);

  /* ==========================================================
     SERVICE DROPDOWN STATE

     TRUE:
     Main service dropdown is currently open.

     FALSE:
     Main service dropdown is closed.
  ========================================================== */

  const [serviceSelectOpen, setServiceSelectOpen] =
    useState(false);

  /* ==========================================================
     EMAILJS CONFIGURATION
  ========================================================== */

  const PUBLIC_KEY =
    process.env.REACT_APP_EMAILJS_ENQUIRY_PUBLIC_KEY;

  const ENQUIRY_SERVICE_ID =
    process.env.REACT_APP_EMAILJS_ENQUIRY_SERVICE_ID;

  const TEMPLATE_ID =
    process.env.REACT_APP_EMAILJS_ENQUIRY_TEMPLATE_ID;

  const ENQUIRY_EMAIL =
    process.env.REACT_APP_ENQUIRY_EMAIL;

  /* ==========================================================
     HANDLE INPUT
  ========================================================== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    /* --------------------------------------------------------
       MAIN SERVICE CHANGE

       When a new service is selected:

       1. Update service
       2. Clear previous sub-service
       3. Close dropdown state
       4. Sub-service appears after dropdown closes
    -------------------------------------------------------- */

    if (name === "service") {
      setFormData((prev) => ({
        ...prev,
        service: value,
        subService: "",
      }));

      setServiceSelectOpen(false);

      return;
    }

    /* --------------------------------------------------------
       OTHER FIELDS
    -------------------------------------------------------- */

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ==========================================================
     PHONE INPUT
  ========================================================== */

  const handlePhoneChange = (e) => {
    const value = e.target.value
      .replace(/\D/g, "")
      .slice(0, 10);

    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));
  };

  /* ==========================================================
     SERVICE DROPDOWN OPEN
  ========================================================== */

  const handleServiceOpen = () => {
    /*
      Hide the sub-service while the native service
      dropdown is open.

      This prevents the form from visually jumping
      while the browser's native dropdown is displayed.
    */

    setServiceSelectOpen(true);
  };

  /* ==========================================================
     SERVICE DROPDOWN CLOSE
  ========================================================== */

  const handleServiceClose = () => {
    setServiceSelectOpen(false);
  };

  /* ==========================================================
     SEND ENQUIRY
  ========================================================== */

  const sendEnquiry = async (data) => {
    try {
      /* ======================================================
         CONFIGURATION VALIDATION
      ====================================================== */

      if (!PUBLIC_KEY) {
        throw new Error(
          "EmailJS enquiry public key is missing."
        );
      }

      if (!ENQUIRY_SERVICE_ID) {
        throw new Error(
          "EmailJS enquiry service ID is missing."
        );
      }

      if (!TEMPLATE_ID) {
        throw new Error(
          "EmailJS enquiry template ID is missing."
        );
      }

      if (!ENQUIRY_EMAIL) {
        throw new Error(
          "REACT_APP_ENQUIRY_EMAIL is missing from .env"
        );
      }

      /* ======================================================
         FORM DATA
      ====================================================== */

      const name =
        data?.name?.trim() || "";

      const email =
        data?.email?.trim() || "";

      const phone =
        data?.phone?.trim() || "";

      const serviceTitle =
        data?.service?.trim() || "";

      const subService =
        data?.subService?.trim() || "";

      const message =
        data?.message?.trim() || "";

      /* ======================================================
         EMAILJS TEMPLATE PARAMETERS
      ====================================================== */

      const templateParams = {
        /* ----------------------------------------------------
           RECIPIENT
        ---------------------------------------------------- */

        to_email: ENQUIRY_EMAIL,

        /* ----------------------------------------------------
           CUSTOMER INFORMATION
        ---------------------------------------------------- */

        from_name: name,

        from_email: email,

        phone: phone,

        /* ----------------------------------------------------
           SERVICE INFORMATION
        ---------------------------------------------------- */

        service_title: serviceTitle,

        sub_service: subService,

        /* ----------------------------------------------------
           CUSTOMER MESSAGE
        ---------------------------------------------------- */

        message: message,

        /* ----------------------------------------------------
           COMPATIBILITY PARAMETERS

           These allow your existing EmailJS template to work
           even if it uses the older variable names.
        ---------------------------------------------------- */

        name: name,

        email: email,

        service: serviceTitle,

        subService: subService,

        /* ----------------------------------------------------
           OPTIONAL PARAMETERS
        ---------------------------------------------------- */

        experience: data?.experience || "",

        skills: data?.skills || "",

        resume: data?.resume || "",
      };

      /* ======================================================
         DEBUG
      ====================================================== */

      console.log(
        "EmailJS Enquiry Template Params:",
        templateParams
      );

      /* ======================================================
         SEND EMAIL
      ====================================================== */

      const response = await emailjs.send(
        ENQUIRY_SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );

      /* ======================================================
         SUCCESS
      ====================================================== */

      console.log(
        "Enquiry email sent successfully:",
        response
      );

      return {
        success: true,
        response,
      };
    } catch (error) {
      /* ======================================================
         ERROR
      ====================================================== */

      console.error(
        "EmailJS Enquiry Error:",
        error
      );

      return {
        success: false,
        error,
      };
    }
  };

  /* ==========================================================
     SUBMIT
  ========================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    /* ========================================================
       VALIDATION
    ======================================================== */

    if (!formData.name.trim()) {
      console.warn("Please enter your name.");
      return;
    }

    if (!formData.email.trim()) {
      console.warn("Please enter your email.");
      return;
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      console.warn("Please enter a valid email.");
      return;
    }

    if (
      !formData.phone ||
      formData.phone.length !== 10
    ) {
      console.warn(
        "Please enter a valid 10-digit phone number."
      );
      return;
    }

    if (!formData.service) {
      console.warn("Please select a service.");
      return;
    }

    if (
      subServices.length > 0 &&
      !formData.subService
    ) {
      console.warn(
        "Please select a sub-service."
      );
      return;
    }

    if (!formData.message.trim()) {
      console.warn("Please enter your message.");
      return;
    }

    /* ========================================================
       START LOADING
    ======================================================== */

    setLoading(true);

    try {
      /* ======================================================
         SEND EMAIL
      ====================================================== */

      const result = await sendEnquiry(formData);

      /* ======================================================
         EMAIL SENT
      ====================================================== */

      if (result.success) {
        /* -----------------------------------------------
           SHOW PREMIUM SUCCESS CARD
        ----------------------------------------------- */

        setSubmitted(true);

        /* -----------------------------------------------
           RESET FORM
        ----------------------------------------------- */

        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          subService: "",
          message: "",
        });

        setServiceSelectOpen(false);

        /* -----------------------------------------------
           AUTO CLOSE SUCCESS CARD
        ----------------------------------------------- */

        setTimeout(() => {
          setSubmitted(false);
        }, 3500);
      } else {
        console.error(
          "Unable to send enquiry:",
          result.error
        );
      }
    } catch (error) {
      console.error(
        "Unexpected enquiry error:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     CURRENT SUB-SERVICES
  ========================================================== */

  const subServices = formData.service
    ? serviceData[formData.service] || []
    : [];

  /* ============================================================
     RETURN
  ============================================================ */

  return (
    <>
      {/* ======================================================
          SUCCESS OVERLAY
      ====================================================== */}

      <AnimatePresence>
        {submitted && (
          <motion.div
            className="
              fixed
              inset-0
              z-[9999]
              flex
              items-center
              justify-center
              px-4
            "
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              duration: 0.25,
            }}
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--text-primary) 18%, transparent)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
            }}
          >
            {/* ==================================================
                PREMIUM SUCCESS CARD
            ================================================== */}

            <motion.div
              initial={{
                opacity: 0,
                y: 18,
                scale: 0.96,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                y: 12,
                scale: 0.97,
              }}
              transition={{
                duration: 0.35,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                relative
                w-full
                max-w-[380px]
                overflow-hidden
                rounded-2xl
                border
                p-6
                text-center
                shadow-2xl
                sm:p-7
              "
              style={{
                backgroundColor: "var(--card-bg)",
                borderColor: "var(--border-primary)",
                color: "var(--text-primary)",
              }}
            >
              {/* TOP GLOW */}

              <div
                className="
                  pointer-events-none
                  absolute
                  left-1/2
                  top-0
                  h-28
                  w-28
                  -translate-x-1/2
                  -translate-y-1/2
                  rounded-full
                  blur-3xl
                "
                style={{
                  backgroundColor:
                    "color-mix(in srgb, var(--brand-primary) 25%, transparent)",
                }}
              />

              {/* CLOSE BUTTON */}

              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="
                  absolute
                  right-3
                  top-3
                  flex
                  h-7
                  w-7
                  items-center
                  justify-center
                  rounded-full
                  transition-all
                  duration-200
                  hover:scale-105
                "
                style={{
                  backgroundColor:
                    "var(--bg-secondary)",
                  color: "var(--text-muted)",
                }}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>

              {/* SUCCESS ICON */}

              <div
                className="
                  relative
                  mx-auto
                  mb-4
                  flex
                  h-14
                  w-14
                  items-center
                  justify-center
                  rounded-full
                "
                style={{
                  backgroundColor:
                    "color-mix(in srgb, var(--brand-primary) 12%, transparent)",
                  color: "var(--brand-primary)",
                }}
              >
                <CheckCircle2
                  className="h-7 w-7"
                  strokeWidth={1.8}
                />
              </div>

              {/* TITLE */}

              <h3
                className="
                  relative
                  text-xl
                  font-semibold
                  tracking-tight
                  sm:text-[22px]
                "
              >
                Message Sent Successfully
              </h3>

              {/* DESCRIPTION */}

              <p
                className="
                  relative
                  mx-auto
                  mt-2
                  max-w-[300px]
                  text-sm
                  leading-6
                "
                style={{
                  color: "var(--text-muted)",
                }}
              >
                Thank you for reaching out. Our team
                will contact you shortly.
              </p>

              {/* BRAND LINE */}

              <div
                className="
                  relative
                  mx-auto
                  mt-5
                  h-[2px]
                  w-12
                  rounded-full
                "
                style={{
                  backgroundColor:
                    "var(--brand-primary)",
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ======================================================
          MAIN SECTION
      ====================================================== */}

      <section
        id="contact-form"
        className="
          w-full
          overflow-hidden
          px-4
          py-9
          min-[375px]:px-5
          min-[375px]:py-10
          sm:px-6
          sm:py-12
          md:px-8
          md:py-14
          lg:px-10
          lg:py-14
          xl:px-12
        "
        style={{
          background: "var(--bg-secondary)",
        }}
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-7xl
            grid-cols-1
            items-start
            gap-5
            lg:grid-cols-2
            lg:gap-5
          "
        >
          {/* ====================================================
              LEFT — CONTACT FORM
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: -30,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              flex
              min-w-0
              flex-col
              gap-4
              rounded-2xl
              p-5
              min-[375px]:gap-5
              min-[375px]:p-6
              sm:p-7
              md:p-7
              lg:p-7
              xl:p-8
            "
            style={{
              background: "var(--card-bg)",
              border:
                "1px solid var(--border-primary)",
              color: "var(--text-primary)",
            }}
          >
            {/* ==================================================
                HEADING
            ================================================== */}

            <AnimatedHeading
              as="h2"
              parts={["Send us a Message"]}
              className="
                mb-1
                font-saira
                text-2xl
                font-semibold
                leading-[1.15]
                text-[var(--text-primary)]
                min-[375px]:text-[26px]
                sm:text-3xl
                md:text-4xl
                lg:mb-4
                lg:text-5xl
                lg:leading-tight
              "
            />

            {/* ==================================================
                FORM
            ================================================== */}

            <form
              onSubmit={handleSubmit}
              className="
                flex
                min-w-0
                flex-col
                gap-3
                min-[375px]:gap-3.5
                sm:gap-4
              "
            >
              {/* =================================================
                  ROW 1
              ================================================= */}

              <div
                className="
                  grid
                  min-w-0
                  grid-cols-1
                  gap-3
                  min-[375px]:gap-3.5
                  sm:grid-cols-2
                  sm:gap-4
                "
              >
                {/* NAME */}

                <motion.div
                  variants={fadeUp(0.07)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                  }}
                  className="min-w-0"
                >
                  <Field
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Name*"
                    required
                  />
                </motion.div>

                {/* EMAIL */}

                <motion.div
                  variants={fadeUp(0.1)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                  }}
                  className="min-w-0"
                >
                  <Field
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email*"
                    type="email"
                    required
                  />
                </motion.div>
              </div>

              {/* =================================================
                  ROW 2
              ================================================= */}

              <div
                className="
                  grid
                  min-w-0
                  grid-cols-1
                  gap-3
                  min-[375px]:gap-3.5
                  sm:grid-cols-2
                  sm:gap-4
                "
              >
                {/* PHONE */}

                <motion.div
                  variants={fadeUp(0.13)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                  }}
                  className="min-w-0"
                >
                  <Field
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="Phone*"
                    type="tel"
                    required
                    inputMode="numeric"
                    maxLength={10}
                  />
                </motion.div>

                {/* MAIN SERVICE */}

                <motion.div
                  variants={fadeUp(0.16)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{
                    once: true,
                  }}
                  className="min-w-0"
                >
                  <ServiceSelect
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    placeholder="Select Service*"
                    options={Object.keys(serviceData)}
                    onOpen={handleServiceOpen}
                    onClose={handleServiceClose}
                  />
                </motion.div>
              </div>

              {/* =================================================
                  SUB SERVICE

                  IMPORTANT:

                  There is NO empty placeholder here.

                  Therefore:
                  - No service = no space
                  - Dropdown open = no space
                  - Service selected + dropdown closed =
                    sub-service appears and form grows naturally
              ================================================= */}

              <AnimatePresence initial={false}>
                {formData.service &&
                  subServices.length > 0 &&
                  !serviceSelectOpen && (
                    <motion.div
                      key={`subservice-${formData.service}`}
                      initial={{
                        opacity: 0,
                        height: 0,
                        y: -6,
                      }}
                      animate={{
                        opacity: 1,
                        height: "auto",
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        height: 0,
                        y: -6,
                      }}
                      transition={{
                        duration: 0.22,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="
                        min-w-0
                        overflow-hidden
                      "
                    >
                      <ServiceSelect
                        name="subService"
                        value={formData.subService}
                        onChange={handleChange}
                        placeholder="Select Sub-Service*"
                        options={subServices}
                      />
                    </motion.div>
                  )}
              </AnimatePresence>

              {/* =================================================
                  MESSAGE
              ================================================= */}

              <motion.div
                variants={fadeUp(0.22)}
                initial="hidden"
                whileInView="visible"
                viewport={{
                  once: true,
                }}
                className="min-w-0"
              >
                <Field
                  as="textarea"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message*"
                  required
                  className="
                    min-h-[120px]
                    resize-none
                    min-[375px]:min-h-[130px]
                    sm:min-h-[140px]
                    md:min-h-[150px]
                    lg:min-h-[155px]
                  "
                />
              </motion.div>

              {/* =================================================
                  SUBMIT BUTTON
              ================================================= */}

              <motion.div
                variants={fadeUp(0.25)}
                initial="hidden"
                whileInView="visible"
                viewport={{
                  once: true,
                }}
                className="
                  flex
                  w-full
                  pt-1
                  sm:pt-1.5
                "
              >
                <button
                  type="submit"
                  disabled={loading}
                  className="
                    group
                    relative
                    inline-flex
                    w-full
                    items-center
                    justify-center
                    overflow-hidden
                    rounded-full
                    border
                    border-transparent
                    px-4
                    py-2.5
                    font-semibold
                    shadow-md
                    transition-all
                    duration-300
                    min-[375px]:py-3
                    sm:w-auto
                    sm:px-5
                    sm:py-2.5
                    md:px-6
                    hover:scale-[1.02]
                    active:scale-95
                    disabled:cursor-not-allowed
                    disabled:opacity-60
                    disabled:hover:scale-100
                  "
                  style={{
                    backgroundColor:
                      "var(--brand-primary)",
                    color: "#000000",
                  }}
                >
                  {/* HOVER BACKGROUND */}

                  <span
                    aria-hidden
                    className="
                      absolute
                      inset-0
                      w-0
                      transition-all
                      duration-500
                      ease-out
                      group-hover:w-full
                    "
                    style={{
                      backgroundColor:
                        "var(--bg-primary)",
                    }}
                  />

                  {/* BUTTON CONTENT */}

                  <span
                    className="
                      relative
                      z-10
                      flex
                      items-center
                      justify-center
                      gap-2
                      text-sm
                      min-[375px]:gap-2.5
                      sm:text-base
                      md:gap-3
                    "
                  >
                    {loading
                      ? "Sending..."
                      : "Submit Now"}

                    {!loading && (
                      <span
                        className="
                          inline-flex
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          p-1
                          transition-all
                          duration-500
                          min-[375px]:p-1.5
                          md:p-2
                          group-hover:translate-x-1
                          group-hover:rotate-45
                        "
                        style={{
                          backgroundColor:
                            "var(--bg-primary)",
                          color:
                            "var(--text-primary)",
                        }}
                      >
                        <ArrowUpRight
                          className="
                            h-4
                            w-4
                            min-[375px]:h-[18px]
                            min-[375px]:w-[18px]
                            md:h-5
                            md:w-5
                          "
                        />
                      </span>
                    )}
                  </span>
                </button>
              </motion.div>
            </form>
          </motion.div>

          {/* ====================================================
              RIGHT — GOOGLE MAP

              IMPORTANT:
              The map has its own fixed/minimum height and
              does NOT stretch with the form.

              Therefore when Sub-Service appears:
              ONLY the form becomes taller.
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: 30,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.6,
              delay: 0.1,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              h-[300px]
              w-full
              min-w-0
              self-start
              overflow-hidden
              rounded-2xl
              min-[375px]:h-[330px]
              sm:h-[380px]
              md:h-[420px]
              lg:h-[540px]
              xl:h-[560px]
            "
            style={{
              border:
                "1px solid var(--brand-primary)",
              backgroundColor:
                "var(--card-bg)",
            }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3743.6089841855123!2d85.81305227500884!3d20.233563281224168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a19a7006c9f7227%3A0x699c8d3114f03a83!2sWeb_Bocket%20Software%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1779510520853!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{
                border: 0,
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Web Bocket Location"
            />
          </motion.div>
        </div>
      </section>
    </>
  );
}